import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, getErrorMessage } from '../../lib/api';
import { toast, alert } from '../../utils/toast';
import type { KongConfiguration } from '../../types';
import { Plug, Route, Wrench, Package, Search, RefreshCw } from 'lucide-react';

interface KongSyncDashboardProps {
  productId: string;
  kongConfigurations: KongConfiguration[];
  onSync: () => void;
}

export const KongSyncDashboard: React.FC<KongSyncDashboardProps> = ({
  productId,
  kongConfigurations,
  onSync,
}) => {
  const syncMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/portal/admin/kong/sync/${productId}`);
    },
    onSuccess: () => {
      onSync();
      toast.success('Sincronização com o Kong Gateway concluída com sucesso!');
    },
    onError: (error) => {
      toast.error(`Falha ao sincronizar: ${getErrorMessage(error)}`);
    },
  });

  const verifyMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/portal/admin/kong/verify', { productId });
      return response.data;
    },
    onSuccess: (data) => {
      if (data.drift_detected) {
        alert.warning(
          'Divergência de Configuração Detetada',
          `${data.differences.length} diferença(s) encontrada(s). Clique em "Sincronizar Agora" para resolver.`
        );
      } else {
        alert.success('Sem Divergências', 'O Kong Gateway está sincronizado com a base de dados.');
      }
      onSync();
    },
    onError: (error) => {
      toast.error(`Falha ao verificar: ${getErrorMessage(error)}`);
    },
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'SYNCED':
        return <span className="badge badge-success">Sincronizado</span>;
      case 'PENDING':
        return <span className="badge badge-warning">Pendente</span>;
      case 'FAILED':
        return <span className="badge badge-danger">Falhou</span>;
      case 'DELETED':
        return <span className="badge badge-gray">Removido</span>;
      default:
        return <span className="badge badge-gray">{status}</span>;
    }
  };

  const getEntityIcon = (type: string) => {
    switch (type) {
      case 'SERVICE':
        return <Plug className="w-4 h-4 text-primary-500" />;
      case 'ROUTE':
        return <Route className="w-4 h-4 text-indigo-500" />;
      case 'PLUGIN':
        return <Wrench className="w-4 h-4 text-amber-500" />;
      default:
        return <Package className="w-4 h-4 text-slate-500" />;
    }
  };

  const groupedConfigs = kongConfigurations.reduce(
    (acc, config) => {
      if (!acc[config.entity_type]) {
        acc[config.entity_type] = [];
      }
      acc[config.entity_type].push(config);
      return acc;
    },
    {} as Record<string, KongConfiguration[]>
  );

  const syncedCount = kongConfigurations.filter((c) => c.sync_status === 'SYNCED').length;
  const failedCount = kongConfigurations.filter((c) => c.sync_status === 'FAILED').length;
  const pendingCount = kongConfigurations.filter((c) => c.sync_status === 'PENDING').length;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <p className="text-sm text-slate-600 dark:text-slate-400">Total de Entidades</p>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">
            {kongConfigurations.length}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-slate-600 dark:text-slate-400">Sincronizadas</p>
          <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{syncedCount}</p>
        </div>
        <div className="card">
          <p className="text-sm text-slate-600 dark:text-slate-400">Pendentes</p>
          <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{pendingCount}</p>
        </div>
        <div className="card">
          <p className="text-sm text-slate-600 dark:text-slate-400">Com Falha</p>
          <p className="text-2xl font-bold text-rose-600 dark:text-rose-400">{failedCount}</p>
        </div>
      </div>

      {/* Actions */}
      <div className="card">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              Sincronização com Kong Gateway
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              Gira os serviços, rotas (`/sandbox`, `/v1` e diretas) e plugins deste produto no Kong
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => verifyMutation.mutate()}
              disabled={verifyMutation.isPending}
              className="btn-secondary inline-flex items-center gap-1.5"
            >
              <Search className="w-4 h-4" />
              <span>{verifyMutation.isPending ? 'A verificar...' : 'Verificar Estado'}</span>
            </button>
            <button
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
              className="btn-primary inline-flex items-center gap-1.5"
            >
              <RefreshCw className={`w-4 h-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
              <span>{syncMutation.isPending ? 'A sincronizar...' : 'Sincronizar Agora'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Configuration List */}
      {kongConfigurations.length > 0 ? (
        <div className="space-y-4">
          {Object.entries(groupedConfigs).map(([entityType, configs]) => (
            <div key={entityType} className="card">
              <h4 className="text-md font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                {getEntityIcon(entityType)}
                <span>
                  {entityType}S ({configs.length})
                </span>
              </h4>
              <div className="space-y-2">
                {configs.map((config, index) => (
                  <div
                    key={config.id || `config-${index}`}
                    className="border border-slate-200 dark:border-slate-700 rounded-xl p-3 hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <code className="text-sm font-medium text-slate-900 dark:text-white">
                            {config.entity_name || config.entity_id || 'Sem nome'}
                          </code>
                          {getStatusBadge(config.sync_status)}
                        </div>

                        {config.entity_id && (
                          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
                            Kong ID:{' '}
                            <code className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                              {config.entity_id}
                            </code>
                          </p>
                        )}

                        {config.last_synced_at && (
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Última sincronização:{' '}
                            {new Date(config.last_synced_at).toLocaleString('pt-PT')}
                          </p>
                        )}

                        {config.sync_error && (
                          <div className="mt-2 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 rounded-lg p-2">
                            <p className="text-xs text-rose-800 dark:text-rose-300">
                              <strong>Erro:</strong> {config.sync_error}
                            </p>
                            <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">
                              Tentativas: {config.sync_attempts}
                            </p>
                          </div>
                        )}

                        {/* Configuration Details */}
                        <details className="mt-2">
                          <summary className="text-xs text-primary-600 dark:text-primary-400 cursor-pointer hover:text-primary-700">
                            Ver configuração técnica
                          </summary>
                          <pre className="mt-2 text-xs bg-slate-50 dark:bg-slate-800/80 text-slate-800 dark:text-slate-200 p-2 rounded-lg border border-slate-200 dark:border-slate-700 overflow-auto max-h-48">
                            {JSON.stringify(config.configuration, null, 2)}
                          </pre>
                        </details>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card text-center py-12">
          <p className="text-slate-600 dark:text-slate-300 mb-2 font-medium">
            Nenhuma configuração Kong encontrada
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Publique o produto ou clique em &quot;Sincronizar Agora&quot; para criar os serviços e
            rotas no Kong automaticamente
          </p>
        </div>
      )}

      {/* Help Text */}
      <div className="card bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/50">
        <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">
          Sobre a Sincronização com o Kong Gateway
        </h4>
        <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-disc list-inside">
          <li>
            <strong>Sincronizar Agora:</strong> Dispara manualmente a criação/atualização dos
            serviços, rotas e plugins deste produto no Kong Gateway
          </li>
          <li>
            <strong>Verificar Estado:</strong> Compara as entidades registadas na base de dados com
            o estado atual em memória no Kong
          </li>
          <li>
            <strong>Auto-Sync:</strong> As rotas são sincronizadas automaticamente sempre que
            publica o produto
          </li>
        </ul>
      </div>
    </div>
  );
};
